import { Anchor, Button, Group, Loader, Paper, Stack, Text, Textarea, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconDeviceFloppy, IconExternalLink, IconUsers } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { AdvancedDisclosure } from '@/components/AdvancedDisclosure'
import { AIInputWrapper } from '@/components/AIInputWrapper'
import { InputLabel } from '@/components/InputLabel'
import { LiveModuleBanner } from '@/components/LiveModuleBanner'
import { TranscriptView } from '@/components/TranscriptView'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { AddStepDataContentModal } from '@/screens/ContentModuleFormScreen/AddStepDataContentModal'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { FAQSection } from '@/screens/ContentModuleFormScreen/FAQSection'
import { IdentifySpeakersModal } from '@/screens/ContentModuleFormScreen/IdentifySpeakersModal'
import { ResourceThumbnailUpload } from '@/screens/ContentModuleFormScreen/ResourceThumbnailUpload'
import { SpeakerList } from '@/screens/ContentModuleFormScreen/SpeakerList'
import { UploadedFilePreview } from '@/screens/ContentModuleFormScreen/UploadedFilePreview'
import { useReviewModalState } from '@/screens/ContentModuleFormScreen/useReviewModalState'
import { isMediaResourceType, type ResourceType } from '@/services/api/api.types'
import * as aiInputClasses from '@/styles/AIInputWrapper.css'
import * as commonClasses from '@/styles/componentCommons.css'
import { isDisplayable } from '@/utils/contentAccess'
import { castStepToZodForm, castZodFormToStep, splitMediaSteps } from '@/utils/contentModuleForm'
import { isStepFieldGenerated } from '@/utils/fieldProvenance'
import { getYouTubeThumbnailUrl } from '@/utils/thumbnails'
import { collectSpeakers, transcriptHasSpeakerLabels } from '@/utils/transcript'

const THUMBNAIL_SUPPORTED_TYPES: ReadonlySet<ResourceType> = new Set<ResourceType>([
  'image',
  'video',
  'audio',
  'pdf',
  'pptx',
  'docx',
  'youtube',
])

const DESCRIPTION_REQUIRED = 'File description is required.'
const DESCRIPTION_NEEDS_TRANSCRIPT = 'Add a transcript above before you can write a description.'

/** "a, b and c" — names what the disclosure holds so it can be judged unopened. */
function formatList(parts: Array<string | false | undefined>): string {
  const items = parts.filter((part): part is string => !!part)
  if (items.length === 0) return ''
  if (items.length === 1) return items[0]
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

export const ReviewStepModal = observer(function ReviewStepModal(props: CMFormSection) {
  const [descriptionError, setDescriptionError] = useState<'required' | 'needs-transcript' | null>(null)
  // The primary's access is resolved for it and it has no picker anywhere — decision 2.
  const isPrimary =
    splitMediaSteps(props.stepsQuery.data, props.cmQuery.data?.primaryStepId).primary?.stepId === props.stepId

  const {
    stepResources,
    stepResourcesSettled,
    pendingFileName,
    setPendingFileName,
    contentAccessType,
    showInApp,
    useInRag,
    pendingDescription,
    setPendingDescription,
    thumbnailDraft,
    markThumbnailReplaced,
    markThumbnailRemoved,
    clearThumbnailDraft,

    descriptionAIState,
    descriptionOutcome,
    descriptionSuggestion,
    generateSourceFileDescription,
    generateSourceFileDescriptionWithGuide,
    acceptDescriptionSuggestion,
    rejectDescriptionSuggestion,
    cancelDescriptionGeneration,

    autoGenerationTriggeredRef,
    descriptionAutoGenTriggeredRef,
    youtubeTitleSeedRef,
    faqSectionRef,

    hasContent,
    hasExtractedContent,
    isMutating,
    uploadedFileResource,
    sourceFileResource,
  } = useReviewModalState(props, { isPrimary })

  const { getFieldState, generateFieldAndAccept, acceptStepSuggestion, rejectSuggestion, cancelGeneration } =
    useAIFieldGeneration(props)

  const isYoutube = sourceFileResource?.type === 'youtube'
  // SME-1123: the transcript gate is decision 17 and stays, but its only explanation sat
  // in the transcript panel above and scrolled off screen. Say it at the field too, and
  // only once the content query has answered, or a file that has a transcript spends the
  // request being told to add one.
  const descriptionNeedsTranscript =
    isYoutube && contentAccessType !== 'media_only' && props.stepContentQuery.isFetched && !hasExtractedContent

  const openDataContentModal = useCallback(() => {
    props.openModal?.({
      title: `Edit File Content Manually`,
      subtitle: 'Copy and paste or enter your content manually.',
      Component: AddStepDataContentModal,
      ComponentProps: {
        initialContent: props.stepContentQuery.data?.content ?? '',
      },
      ModalProps: {
        size: '95vw',
      },
    })
  }, [props.stepContentQuery.data, props.openModal])

  const openSpeakersModal = useCallback(() => {
    props.openModal?.({
      title: 'Identify speakers',
      subtitle: 'Match each detected voice to a name so Sidekick can attribute quotes.',
      Component: IdentifySpeakersModal,
      ModalProps: {
        size: theme.rem(1280),
      },
    })
  }, [props.openModal])

  const updateStep = useCallback(async () => {
    // Both checks run before the bail, or a blank title returns first and the description
    // keeps its unmarked border until the title is fixed (SME-1127).
    const nameHasError = props.stepForm.validateField('name').hasError
    const descriptionMissing = !pendingDescription?.trim()

    setDescriptionError(descriptionMissing ? (descriptionNeedsTranscript ? 'needs-transcript' : 'required') : null)

    if (nameHasError || descriptionMissing) {
      notifications.show({
        title: 'Could Not Submit Form',
        message:
          descriptionMissing && !nameHasError
            ? descriptionNeedsTranscript
              ? DESCRIPTION_NEEDS_TRANSCRIPT
              : DESCRIPTION_REQUIRED
            : 'There was an issue submitting file details. Please check the form for errors.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      return
    }

    try {
      await faqSectionRef.current?.saveUnsavedFAQs()

      const formTitle = props.stepForm.getValues().name

      if (pendingFileName && uploadedFileResource) {
        await props.resourceUpdateMutation.mutateAsync({
          resource_id: uploadedFileResource.resourceId,
          step_id: props.stepId,
          file_name: pendingFileName,
          type: uploadedFileResource.type as ResourceType,
          title: formTitle || uploadedFileResource.title,
          description: uploadedFileResource.description,
          keywords: uploadedFileResource.keywords || [],
          show_in_app: showInApp,
          is_approved: true,
        })
      }

      const source = sourceFileResource
      if (source) {
        const hasShowInAppChange = showInApp !== (source.showInApp ?? false)
        const hasDescriptionChange = pendingDescription !== null && pendingDescription !== (source.description ?? '')
        const hasTitleChange = formTitle && formTitle !== source.title
        const thumbnailUpdate =
          thumbnailDraft.kind === 'replaced'
            ? { thumbnail: thumbnailDraft.s3Key }
            : thumbnailDraft.kind === 'removed'
              ? { thumbnail: '' }
              : null

        if (hasShowInAppChange || hasDescriptionChange || hasTitleChange || thumbnailUpdate) {
          await props.resourceUpdateMutation.mutateAsync({
            resource_id: source.resourceId,
            step_id: props.stepId,
            file_name: source.fileName ?? undefined,
            type: source.type as ResourceType,
            title: formTitle || source.title,
            description: pendingDescription ?? source.description,
            keywords: source.keywords || [],
            show_in_app: showInApp,
            is_approved: true,
            ...(thumbnailUpdate ?? {}),
          })
        }
      }

      await new Promise((resolve, reject) => {
        props.stepUpdateMutation.mutate(
          {
            ...castZodFormToStep(props.stepForm.getValues()),
            step_id: props.stepId!,
            use_in_rag: useInRag,
            // The description is edited on the resource, but the step keeps its own copy —
            // it is what module-level generation summarises from, and where the field's
            // provenance lives. Non-blank by here; the save returns early otherwise.
            description: pendingDescription!,
          },
          {
            onSuccess: () => {
              props.signalStepSaved?.()
              props.closeModal(() => {
                props.stepForm.setValues(castStepToZodForm())
                props.faqActions.resetStepFAQState()
                setPendingFileName(null)
                clearThumbnailDraft()
              })
              resolve(undefined)
            },
            onError: reject,
          },
        )
      })
    } catch (error) {
      console.error('Failed to save step:', error)

      if (error instanceof Error && error.message === 'FAQ_VALIDATION_ERROR') {
        return
      }

      notifications.show({
        title: 'Could Not Save Step',
        message: 'There was an issue saving your step and FAQs. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      return
    }
  }, [
    props.stepForm,
    props.resourceUpdateMutation,
    props.stepId,
    props.stepUpdateMutation,
    props.signalStepSaved,
    props.closeModal,
    props.faqActions,
    uploadedFileResource,
    sourceFileResource,
    pendingFileName,
    pendingDescription,
    descriptionNeedsTranscript,
    thumbnailDraft,
    clearThumbnailDraft,
    descriptionAIState,
    getFieldState,
    showInApp,
    useInRag,
    faqSectionRef,
  ])

  const handleCancel = useCallback(() => {
    props.closeModal(() => {
      props.stepForm.setValues(castStepToZodForm())
      props.faqActions.resetStepFAQState()
      clearThumbnailDraft()
    })
  }, [props.closeModal, props.stepForm, props.faqActions, clearThumbnailDraft])

  // Reset form errors when stepId changes
  useEffect(() => {
    props.stepForm.clearErrors()
    autoGenerationTriggeredRef.current = null
  }, [props.stepId])

  // Seed the title from the BE-fetched YouTube video title. Standing down without arming
  // the ref leaves a pending suggestion in charge, and rejecting it falls back to here.
  const nameFieldState = getFieldState('name', 'step')
  useEffect(() => {
    if (!props.stepId || stepResources === undefined) return
    if (youtubeTitleSeedRef.current === props.stepId) return
    if (nameFieldState.state !== 'idle') return

    youtubeTitleSeedRef.current = props.stepId

    if (sourceFileResource?.type !== 'youtube') return

    const videoTitle = sourceFileResource.title?.trim()
    if (!videoTitle) return
    if (props.stepForm.getValues().name?.trim()) return

    props.stepForm.setFieldValue('name', videoTitle)
  }, [props.stepId, props.stepForm, stepResources, sourceFileResource, nameFieldState.state, youtubeTitleSeedRef])

  // Auto-generate title when content is available and processing is complete.
  // Sets the ref guard when the name is already populated, so clearing an existing title
  // does not trigger auto-generation (only newly created steps with blank titles).
  // Waits on resources settling so a YouTube title seeds first; `isError` counts as
  // settled, or a failed fetch would block generation for good.
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const currentName = props.stepForm.getValues().name
    const shouldTrigger = isStepCreated && stepResourcesSettled
    const generationKey = `${props.stepId}-${!!props.stepContentQuery.data}`

    if (props.stepId && hasExtractedContent && shouldTrigger && autoGenerationTriggeredRef.current !== generationKey) {
      autoGenerationTriggeredRef.current = generationKey

      if (currentName) return

      const step = props.stepsQuery.data?.find(s => s.stepId === props.stepId)
      if (step?.titleAi?.trim()) return

      generateFieldAndAccept('step', 'name')
    }
  }, [
    props.stepId,
    props.stepsQuery.data,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    !!props.stepContentQuery.data,
    generateFieldAndAccept,
    props.stepForm.getValues().name,
    hasExtractedContent,
    stepResourcesSettled,
  ])

  // Auto-generate description when description is genuinely empty.
  // Sets the ref guard when description is already populated, so clearing an existing
  // description does not trigger auto-generation.
  useEffect(() => {
    const isStepCreated = props.cmQuery.isSuccess && props.stepsQuery.isSuccess
    const generationKey = `${props.stepId}-${sourceFileResource?.resourceId}-description`
    const descriptionEmpty = !(pendingDescription?.trim() || sourceFileResource?.description?.trim())

    if (
      props.stepId &&
      hasExtractedContent &&
      sourceFileResource !== undefined &&
      isStepCreated &&
      descriptionAIState === 'idle' &&
      descriptionAutoGenTriggeredRef.current !== generationKey
    ) {
      descriptionAutoGenTriggeredRef.current = generationKey

      if (!descriptionEmpty) return

      generateSourceFileDescription()
    }
  }, [
    props.stepId,
    hasExtractedContent,
    pendingDescription,
    descriptionAIState,
    props.cmQuery.isSuccess,
    props.stepsQuery.isSuccess,
    sourceFileResource,
  ])

  // Decision 19: a suggestion is applied, never offered. Guarded so we don't overwrite
  // what the expert wrote — the AI tag is the whole affordance, and it clears on edit.
  useEffect(() => {
    if (nameFieldState.state !== 'pending' || !nameFieldState.suggestion) return
    if (props.stepForm.getValues().name?.trim()) return
    acceptStepSuggestion('name')
  }, [nameFieldState.state, nameFieldState.suggestion, acceptStepSuggestion, props.stepForm])

  useEffect(() => {
    if (descriptionAIState !== 'pending' || !descriptionSuggestion) return
    acceptDescriptionSuggestion()
  }, [descriptionAIState, descriptionSuggestion, acceptDescriptionSuggestion])

  // Without this the message outlives its cause. Adding the transcript it asked for, or
  // an auto-accepted suggestion, both fill the field while the error stays on screen.
  useEffect(() => {
    if (!descriptionError) return
    const resolved =
      descriptionError === 'needs-transcript' ? !descriptionNeedsTranscript : !!pendingDescription?.trim()

    if (resolved) setDescriptionError(null)
  }, [descriptionError, descriptionNeedsTranscript, pendingDescription])

  const currentStep = props.stepsQuery.data?.find(step => step.stepId === props.stepId)
  // Decision 10: the wording follows the file's format. An uploaded .mp4 used to read as
  // a generic file because only YouTube picked the media labels. Gating stays on
  // `isYoutube` on purpose — an upload is transcribed on ingest and a pasted link is not.
  const isMediaFile = isMediaResourceType(sourceFileResource?.type)
  const aiDisabled = !hasContent || (isYoutube && (!hasExtractedContent || contentAccessType === 'media_only'))
  const showTranscript = !isYoutube || contentAccessType !== 'media_only'
  const descriptionDisabled = isYoutube && contentAccessType !== 'media_only' ? !hasExtractedContent : !hasContent
  // Title is editable without a transcript — only the content record it saves against gates it.
  const titleDisabled = !hasContent

  // Decision 2: a primary the app cannot display is Information Only, and the expert is
  // told so rather than left to infer it from a control that isn't there.
  const showInformationOnlyNote =
    isPrimary &&
    hasContent &&
    !isDisplayable({ type: sourceFileResource?.type, showInAppSupported: sourceFileResource?.showInAppSupported })

  // True while BE generation is in flight, or briefly 'pending' before auto-accept.
  const nameAIActive = nameFieldState.state === 'loading' || nameFieldState.state === 'pending'
  const descAIActive = descriptionAIState === 'loading' || descriptionAIState === 'pending'

  const CONTENT_PREVIEW_LIMIT = 2000
  const rawContent = props.stepContentQuery.data?.content ?? ''
  const contentPreview = useMemo(() => {
    if (rawContent.length <= CONTENT_PREVIEW_LIMIT) return rawContent
    return rawContent.slice(0, CONTENT_PREVIEW_LIMIT) + '…'
  }, [rawContent])
  const contentHasSpeakers = useMemo(() => transcriptHasSpeakerLabels(rawContent), [rawContent])
  const speakerNamesMetadata = props.stepContentQuery.data?.speakerNamesMetadata
  const speakers = useMemo(() => collectSpeakers(rawContent, speakerNamesMetadata), [rawContent, speakerNamesMetadata])

  const transcriptPreview = (
    <Paper
      withBorder
      radius="md"
      p="sm"
      style={{ maxHeight: 180, overflow: 'auto', backgroundColor: 'var(--mantine-color-gray-0)' }}>
      <TranscriptView content={rawContent} maxSegments={40} dimmed />
    </Paper>
  )

  // Decision 18: FAQs belong to the file, and a Media Only file has none — its content
  // is never used for answers, and use_in_rag: false de-indexes them anyway.
  const showFAQs = hasContent && contentAccessType !== 'media_only'

  const advancedSummary = formatList([
    !isYoutube && (isMediaFile ? 'transcript' : 'extracted content'),
    !isYoutube && contentHasSpeakers && 'speakers',
    showFAQs && 'FAQs',
  ])

  const thumbnailTargetType = uploadedFileResource?.type ?? sourceFileResource?.type
  const showThumbnailUploader =
    !!sourceFileResource && !!thumbnailTargetType && THUMBNAIL_SUPPORTED_TYPES.has(thumbnailTargetType)

  return (
    <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl">
      {props.isLive && <LiveModuleBanner />}
      {/* Uploaded File Preview */}
      {(uploadedFileResource ||
        (sourceFileResource && (sourceFileResource.type === 'url' || sourceFileResource.type === 'youtube'))) && (
        <Paper shadow="sm" p="md" radius="md">
          <Text size="lg" fw={600} mb={uploadedFileResource ? 'xs' : 'sm'}>
            {isMediaFile ? 'Preview Media' : 'Preview Source Content'}
          </Text>
          <Stack gap="md">
            {uploadedFileResource ? (
              <UploadedFilePreview
                fileName={pendingFileName || uploadedFileResource.fileName || 'Uploaded file'}
                type={uploadedFileResource.type}
                resourceKey={uploadedFileResource.resourceKey}
                resourceData={uploadedFileResource.data}
                onFileNameChange={setPendingFileName}
              />
            ) : (
              <Paper withBorder p="sm" radius="sm" bg="gray.1">
                {sourceFileResource!.data || sourceFileResource!.sourceUrl ? (
                  <Anchor
                    href={sourceFileResource!.data || sourceFileResource!.sourceUrl || ''}
                    target="_blank"
                    size="sm"
                    style={{ display: 'block' }}>
                    <Group gap={6} wrap="nowrap" align="flex-start">
                      <Text
                        component="span"
                        size="sm"
                        style={{ minWidth: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {sourceFileResource!.data || sourceFileResource!.sourceUrl}
                      </Text>
                      <IconExternalLink size={14} style={{ flexShrink: 0, marginTop: 3 }} />
                    </Group>
                  </Anchor>
                ) : (
                  <Text c="dimmed" size="sm">
                    URL unavailable
                  </Text>
                )}
              </Paper>
            )}
            {showInformationOnlyNote && (
              <Text c="dimmed" size="sm">
                This file can&rsquo;t be shown in the app, so it&rsquo;s used for answers only.
              </Text>
            )}
            {showThumbnailUploader && (
              <ResourceThumbnailUpload
                existingThumbnailKey={sourceFileResource!.thumbnailS3Key ?? null}
                fallbackUrl={getYouTubeThumbnailUrl(sourceFileResource!)}
                draft={thumbnailDraft}
                onUploaded={markThumbnailReplaced}
                onRemoved={markThumbnailRemoved}
                disabled={isMutating}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Edit & Configure */}
      <Paper shadow="sm" p="md" radius="md">
        <Stack gap="xxl">
          <Text size="lg" fw={600}>
            {isMediaFile ? 'Edit & Configure Media' : 'Edit & Configure Content'}
          </Text>

          {/* Transcript / Extracted Content (YouTube: before title; others: after description) */}
          {isYoutube && showTranscript && (
            <Stack gap="xs">
              <Group justify="space-between">
                <InputLabel label="Transcript" withAsterisk />
                {hasExtractedContent && (
                  <Button variant="light" size="md" radius="md" onClick={openDataContentModal}>
                    Edit
                  </Button>
                )}
              </Group>
              {hasExtractedContent ? (
                contentHasSpeakers ? (
                  transcriptPreview
                ) : (
                  <Textarea
                    value={contentPreview}
                    readOnly
                    placeholder="Add the transcript for your media so the Sidekick can navigate the media"
                    radius="md"
                    variant="filled"
                    size="sm"
                    rows={6}
                    style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
                  />
                )
              ) : (
                <Paper
                  withBorder
                  p="xl"
                  radius="md"
                  bg="gray.1"
                  style={{
                    minHeight: 150,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  <Stack align="center" gap="sm">
                    <Text size="sm" c="dimmed" ta="center">
                      Add a transcript before you can write a description.
                    </Text>
                    <Button onClick={openDataContentModal} disabled={!hasContent}>
                      Add Transcript
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}

          {/* The wrapper is what shows the AI tag. Its accept/reject controls render only
              at 'pending', which the auto-accept effects above never leave a field at. */}
          <AIInputWrapper
            {...{
              fieldState: getFieldState('name', 'step'),
              onAskSidekick: () => generateFieldAndAccept('step', 'name'),
              onAskSidekickWithGuide: instructions =>
                generateFieldAndAccept('step', 'name', { user_instructions: instructions }),
              onAccept: () => acceptStepSuggestion('name'),
              onReject: () => rejectSuggestion('name', 'step'),
              onCancel: () => cancelGeneration('name', 'step'),
              disabled: aiDisabled,
              onRegenerate: () => generateFieldAndAccept('step', 'name'),
              isGenerated: isStepFieldGenerated(currentStep, 'name', currentStep?.name),
            }}>
            <TextInput
              withAsterisk
              label="Title"
              radius="md"
              disabled={titleDisabled || nameAIActive}
              placeholder={nameAIActive ? 'Generating title…' : undefined}
              rightSection={nameAIActive ? <Loader size="xs" /> : null}
              classNames={nameAIActive ? { input: aiInputClasses.input } : undefined}
              {...props.stepForm.getInputProps('name')}
            />
          </AIInputWrapper>

          <AIInputWrapper
            {...{
              fieldState: {
                state: descriptionAIState,
                suggestion: descriptionSuggestion,
                lastOutcome: descriptionOutcome,
              },
              onAskSidekick: generateSourceFileDescription,
              onAskSidekickWithGuide: instructions => generateSourceFileDescriptionWithGuide(instructions),
              onAccept: () => {
                acceptDescriptionSuggestion()
                setDescriptionError(null)
              },
              onReject: rejectDescriptionSuggestion,
              onCancel: cancelDescriptionGeneration,
              disabled: aiDisabled,
              onRegenerate: generateSourceFileDescription,
              isGenerated: isStepFieldGenerated(currentStep, 'description', currentStep?.description),
            }}>
            <Textarea
              label={isMediaFile ? 'Description' : 'File Description'}
              withAsterisk
              description={descriptionNeedsTranscript ? DESCRIPTION_NEEDS_TRANSCRIPT : undefined}
              placeholder={
                descAIActive
                  ? 'Generating description…'
                  : descriptionNeedsTranscript
                    ? 'Add a transcript first'
                    : 'Enter a description for this content'
              }
              value={pendingDescription ?? ''}
              onChange={event => {
                setPendingDescription(event.currentTarget.value)
                if (descriptionError) setDescriptionError(null)
              }}
              error={
                descriptionError === 'needs-transcript'
                  ? DESCRIPTION_NEEDS_TRANSCRIPT
                  : descriptionError && DESCRIPTION_REQUIRED
              }
              minRows={3}
              autosize
              disabled={descriptionDisabled || descAIActive}
              rightSection={descAIActive ? <Loader size="xs" /> : null}
              rightSectionPointerEvents={descAIActive ? 'none' : undefined}
              classNames={descAIActive ? { input: aiInputClasses.input } : undefined}
            />
          </AIInputWrapper>
        </Stack>
      </Paper>

      {/* Advanced: what her spec puts behind a disclosure. Real work for some files and
          noise for most, so it opens closed every time. The YouTube transcript stays
          outside it — decision 17 makes adding it the expert's next step, and it is the
          one thing they came here to do. */}
      {advancedSummary && (
        <Paper shadow="sm" p="md" radius="md">
          <AdvancedDisclosure summary={advancedSummary}>
            <Stack gap="xxl">
              {!isYoutube && (
                <Stack gap="xs">
                  <Group justify="space-between">
                    <InputLabel label={isMediaFile ? 'Transcript' : 'Extracted Content'} withAsterisk />
                    <Button variant="light" size="md" radius="md" onClick={openDataContentModal} disabled={!hasContent}>
                      Edit
                    </Button>
                  </Group>
                  {contentHasSpeakers ? (
                    transcriptPreview
                  ) : (
                    <Textarea
                      value={contentPreview}
                      disabled
                      placeholder="Enter your content here"
                      radius="md"
                      variant="filled"
                      size="sm"
                      rows={6}
                      style={{ border: '1px solid var(--mantine-color-gray-3)', borderRadius: theme.radius.md }}
                    />
                  )}
                  {contentHasSpeakers && (
                    <SpeakerList
                      mt="md"
                      speakers={speakers}
                      action={
                        <Button
                          variant="light"
                          size="md"
                          radius="md"
                          leftSection={<IconUsers size={18} />}
                          onClick={openSpeakersModal}>
                          Identify Speakers
                        </Button>
                      }
                    />
                  )}
                </Stack>
              )}

              {showFAQs && (
                <Stack gap="xs">
                  <Text size="lg" fw={600}>
                    Add FAQs
                  </Text>
                  <FAQSection {...props} type="step" ref={faqSectionRef} disabled={aiDisabled} />
                </Stack>
              )}
            </Stack>
          </AdvancedDisclosure>
        </Paper>
      )}

      <Group gap="lg" justify="flex-end">
        <Button variant="light" color="gray" radius="md" disabled={isMutating} onClick={handleCancel}>
          Cancel
        </Button>

        <Button
          leftSection={<IconDeviceFloppy />}
          onClick={updateStep}
          disabled={!hasContent || isMutating}
          loading={isMutating}
          radius="md">
          Save and Exit
        </Button>
      </Group>
    </Stack>
  )
})
